/**
 * Public Chat Container
 *
 * The chat window of the entry page: right under the page's message, inviting the question
 * "is it worth it for me?". The agent answers and puts cards into the conversation — the sign-up
 * card is the goal of every conversation.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Snackbar, TextField, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { usePublicChat } from '../hooks/use-public-chat';
import { SignupCard } from '../cards/SignupCard';
import { LoginCard } from '../cards/LoginCard';
import { PricingCard, SafetyCard, SavingsCard, StepsCard } from '../cards/InfoCards';
import type { CardType, PublicMessage } from '../public-chat.types';
import { useEntry } from '../entry-context';
import type { ChatEntry } from '../entry';

type Chat = ReturnType<typeof usePublicChat>;

interface PublicChatContainerProps {
  chat: Chat;
  /** Sends a message; `chip` when it came from a suggestion button. */
  onAsk: (content: string, chip: boolean) => void;
  /** Phone layout: no fixed height, the page scrolls. */
  compact?: boolean;
}

/** Suggestions per entry, in the order of the argument: worth it → how → price → safety. */
const CHIPS: Record<ChatEntry, string[]> = {
  direkt: ['worth', 'how', 'price', 'sales'],
  c: ['worth', 'how', 'price', 'safety'],
  d: ['worth', 'how', 'price', 'safety'],
  e: ['worth', 'how', 'price', 'safety'],
  h: ['worth', 'how', 'price', 'safety'],
  meta: ['worth', 'how', 'price', 'safety'],
  suche: ['worth', 'how', 'price', 'safety'],
  login: ['login', 'worth', 'price'],
};

const AgentAvatar: React.FC<{ size?: number }> = ({ size = 32 }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.avatar} sx={{ width: size, height: size }}>
      <img src="/dashboard/images/logos/product_logo_white.svg" alt="" className={classes.avatarImage} />
    </Box>
  );
};

export const PublicChatContainer: React.FC<PublicChatContainerProps> = ({ chat, onAsk, compact = false }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { entry, openAuth } = useEntry();
  const threadRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const { messages, isLoading, isSending, error, clearError, restart } = chat;

  // Keep the newest message in view inside the chat window, without scrolling the whole page.
  useEffect(() => {
    const el = threadRef.current;
    if (el && messages.length > 0) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const asked = useMemo(() => new Set(messages.filter((m) => m.role === 'user').map((m) => m.content.trim())), [messages]);
  const chips = CHIPS[entry]
    .map((key) => t(`entry.chip.${key}`))
    .filter((label) => !asked.has(label));

  const send = () => {
    const value = inputValue.trim();
    if (!value || isSending) return;
    setInputValue('');
    onAsk(value, false);
  };

  const renderCard = (cardType: CardType) => {
    switch (cardType) {
      case 'SHOW_SIGNUP':
        return (
          <Box key={cardType} className={classes.cardFrame}>
            <SignupCard place="chat" trackView title={t('entry.inlineSignupTitle')} onSwitchToLogin={() => openAuth('login', 'chat_card')} />
          </Box>
        );
      case 'SHOW_LOGIN':
        return (
          <Box key={cardType} className={classes.cardFrame}>
            <LoginCard place="chat" onSwitchToSignup={() => openAuth('signup', 'chat_card')} />
          </Box>
        );
      case 'SHOW_PRICING':
        return <PricingCard key={cardType} />;
      case 'SHOW_SAVINGS':
        return <SavingsCard key={cardType} />;
      case 'SHOW_SAFETY':
        return <SafetyCard key={cardType} />;
      case 'SHOW_FEATURES':
        return <StepsCard key={cardType} />;
      default:
        return null;
    }
  };

  const renderMessage = (message: PublicMessage) => {
    const isUser = message.role === 'user';
    const cards = (message.metadata?.cards || []) as CardType[];
    if (isUser) {
      return (
        <Box key={message.id} className={classes.userRow}>
          <Box className={classes.userBubble}>{message.content}</Box>
        </Box>
      );
    }
    if (message.isStreaming && !message.content) return null;
    return (
      <Box key={message.id} className={classes.agentRow}>
        <AgentAvatar />
        <Box className={classes.agentColumn}>
          <Box className={classes.agentBubble}>
            <ReactMarkdown className={classes.markdown}>{message.content}</ReactMarkdown>
          </Box>
          {!message.isStreaming && cards.map((c) => renderCard(c))}
        </Box>
      </Box>
    );
  };

  const showTyping = isSending && !messages.some((m) => m.isStreaming && m.content);

  return (
    <Box className={classes.panel} component="section" aria-label={t('entry.chatLabel')}>
      <Box className={classes.header}>
        <AgentAvatar size={36} />
        <Box className={classes.headerText}>
          <Typography className={classes.agentName}>{t('entry.agentName')}</Typography>
          <Typography className={classes.online}>
            <span className={classes.onlineDot} />
            {t('entry.online')}
          </Typography>
        </Box>
        {messages.length > 0 && (
          <Button size="small" onClick={() => void restart()} className={classes.restart}>
            {t('entry.restart')}
          </Button>
        )}
      </Box>

      <Box
        ref={threadRef}
        className={cx(
          classes.thread,
          compact && classes.threadCompact,
          compact && messages.length === 0 && !showTyping && classes.threadCompactEmpty
        )}
      >
        <Box className={classes.agentRow}>
          <AgentAvatar />
          <Box className={classes.agentColumn}>
            <Box className={classes.agentBubble}>
              <Typography className={classes.greeting}>{t(`entry.${entry}.greet`)}</Typography>
            </Box>
            {entry === 'c' && <SavingsCard />}
          </Box>
        </Box>
        {isLoading ? (
          <Box className={classes.loading}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          messages.map(renderMessage)
        )}
        {showTyping && (
          <Box className={classes.agentRow}>
            <AgentAvatar />
            <Box className={cx(classes.agentBubble, classes.typing)}>
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
              <span className={classes.typingDot} />
            </Box>
          </Box>
        )}
      </Box>

      <Box className={classes.footer}>
        {chips.length > 0 && (
          <Box className={classes.chips}>
            {chips.map((label) => (
              <Button key={label} variant="outlined" size="small" className={classes.chip} disabled={isSending || isLoading} onClick={() => onAsk(label, true)}>
                {label}
              </Button>
            ))}
          </Box>
        )}
        <Box className={classes.inputRow}>
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t(`entry.${entry}.placeholder`)}
            inputProps={{ 'aria-label': t('entry.inputLabel') }}
            variant="standard"
            fullWidth
            multiline
            maxRows={4}
            disabled={isLoading}
            InputProps={{ disableUnderline: true, className: classes.inputField }}
          />
          <IconButton onClick={send} disabled={!inputValue.trim() || isSending} className={classes.sendButton} aria-label={t('entry.send')}>
            {isSending ? <CircularProgress size={22} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={clearError} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clearError} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    width: '100%',
    minWidth: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 22,
    boxShadow: '0 24px 60px -30px rgba(20, 18, 58, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2.5),
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  agentName: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  online: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.75rem',
    color: theme.palette.success.dark,
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.success.main,
  },
  restart: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  avatar: {
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  avatarImage: {
    width: '58%',
    height: '58%',
  },
  thread: {
    height: 480,
    overflowY: 'auto',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  threadCompact: {
    height: 'auto',
    minHeight: 260,
    maxHeight: '62vh',
    padding: theme.spacing(1.75),
  },
  // Phone, before the first question: only the greeting. The reserved 260 px pushed the
  // question chips and the input below the first screen.
  threadCompactEmpty: {
    minHeight: 0,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  agentRow: {
    display: 'flex',
    gap: theme.spacing(1.25),
    alignItems: 'flex-start',
  },
  agentColumn: {
    flex: 1,
    minWidth: 0,
    maxWidth: 640,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  agentBubble: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '4px 18px 18px 18px',
    padding: theme.spacing(1.5, 2),
    color: theme.palette.text.primary,
    fontSize: '0.95rem',
    lineHeight: 1.55,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  greeting: {
    fontSize: '0.95rem',
    lineHeight: 1.55,
  },
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userBubble: {
    maxWidth: '80%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '18px 4px 18px 18px',
    padding: theme.spacing(1.25, 1.75),
    fontSize: '0.95rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  cardFrame: {
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 16,
    padding: theme.spacing(2),
    maxWidth: 460,
    width: '100%',
    boxSizing: 'border-box',
  },
  markdown: {
    '& p': {
      margin: 0,
      '&:not(:last-child)': {
        marginBottom: theme.spacing(1),
      },
    },
    '& ul, & ol': {
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2.5),
    },
    '& a': {
      color: theme.palette.primary.main,
    },
    '& strong': {
      fontWeight: 600,
    },
  },
  typing: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.light,
    animation: 'typingPulse 1.4s infinite',
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
    '@keyframes typingPulse': {
      '0%, 60%, 100%': { opacity: 0.4, transform: 'scale(1)' },
      '30%': { opacity: 1, transform: 'scale(1.2)' },
    },
  },
  footer: {
    padding: theme.spacing(1.5, 2, 2),
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    textTransform: 'none',
    borderRadius: 22,
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.75),
    [theme.breakpoints.down('md')]: {
      minHeight: 40,
    },
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 14,
    padding: theme.spacing(0.75, 0.75, 0.75, 2),
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  inputField: {
    fontSize: '1rem',
    padding: theme.spacing(1, 0),
  },
  sendButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.greys.lighter,
      color: theme.palette.greys.medium,
    },
  },
}));

export default PublicChatContainer;
